/**
 * Visit list and detail state management.
 *
 * Uses Zustand with immer for immutable updates.
 * Works with branded types from @assistmd/types.
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { VisitId, VisitStatus, ConsentState } from '@assistmd/types';
import { visits as visitsApi } from '@/lib/api/api-client';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface VisitListItem {
  visitId: VisitId;
  patientCode: string;
  patientName?: string;
  status: VisitStatus;
  consentState: ConsentState;
  isRecording: boolean;
  wordCount: number;
  segmentCount: number;
  createdAt: number;
  updatedAt: number;
  closedAt: number | null;
  totalRecordingMs: number;
}

export interface VisitStoreState {
  /** All visits for the list view */
  visits: VisitListItem[];
  /** Currently selected visit ID */
  activeVisitId: VisitId | null;
  /** Loading state for the visit list */
  loading: boolean;
  /** Error message */
  error: string | null;
  /** Search/filter query */
  searchQuery: string;
  /** Status filter */
  statusFilter: VisitStatus | 'all';
}

interface VisitActions {
  setVisits: (visits: VisitListItem[]) => void;
  addVisit: (visit: VisitListItem) => void;
  updateVisit: (visitId: VisitId, patch: Partial<VisitListItem>) => void;
  removeVisit: (visitId: VisitId) => void;
  setActiveVisitId: (id: VisitId | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setSearchQuery: (query: string) => void;
  setStatusFilter: (filter: VisitStatus | 'all') => void;
  fetchVisits: () => Promise<void>;
  clear: () => void;
}

type VisitStore = VisitStoreState & VisitActions;

// ─────────────────────────────────────────────────────────────────────────────
// Store
// ─────────────────────────────────────────────────────────────────────────────

const initialState: VisitStoreState = {
  visits: [],
  activeVisitId: null,
  loading: false,
  error: null,
  searchQuery: '',
  statusFilter: 'all',
};

export const useVisitStore = create<VisitStore>()(
  immer((set) => ({
    ...initialState,

    setVisits: (visits) => {
      set((state) => {
        state.visits = visits;
        state.loading = false;
        state.error = null;
      });
    },

    addVisit: (visit) => {
      set((state) => {
        const idx = state.visits.findIndex((v) => v.visitId === visit.visitId);
        if (idx >= 0) {
          state.visits[idx] = visit;
        } else {
          state.visits.unshift(visit);
        }
      });
    },

    updateVisit: (visitId, patch) => {
      set((state) => {
        const idx = state.visits.findIndex((v) => v.visitId === visitId);
        if (idx >= 0) {
          Object.assign(state.visits[idx]!, patch);
        }
      });
    },

    removeVisit: (visitId) => {
      set((state) => {
        state.visits = state.visits.filter((v) => v.visitId !== visitId);
      });
    },

    setActiveVisitId: (id) => {
      set((state) => {
        state.activeVisitId = id;
      });
    },

    setLoading: (loading) => {
      set((state) => {
        state.loading = loading;
      });
    },

    setError: (error) => {
      set((state) => {
        state.error = error;
        state.loading = false;
      });
    },

    setSearchQuery: (query) => {
      set((state) => {
        state.searchQuery = query;
      });
    },

    setStatusFilter: (filter) => {
      set((state) => {
        state.statusFilter = filter;
      });
    },

    fetchVisits: async () => {
      set((state) => { state.loading = true; state.error = null; });
      try {
        const data = await visitsApi.list();
        const visits = (data as VisitListItem[]) ?? [];
        set((state) => {
          state.visits = visits;
          state.loading = false;
          state.error = null;
        });
      } catch (err) {
        set((state) => {
          state.error = err instanceof Error ? err.message : 'Failed to load visits';
          state.loading = false;
        });
      }
    },

    clear: () => {
      set(() => ({ ...initialState }));
    },
  })),
);

// ─────────────────────────────────────────────────────────────────────────────
// Selectors
// ─────────────────────────────────────────────────────────────────────────────

export function selectFilteredVisits(state: VisitStore): VisitListItem[] {
  let result = state.visits;

  if (state.statusFilter !== 'all') {
    result = result.filter((v) => v.status === state.statusFilter);
  }

  if (state.searchQuery.trim()) {
    const query = state.searchQuery.toLowerCase();
    result = result.filter(
      (v) =>
        v.patientCode.toLowerCase().includes(query) ||
        v.patientName?.toLowerCase().includes(query),
    );
  }

  return result;
}
